import React, { Component, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Spin, Modal, Icon, Divider, Tooltip } from 'antd';
import moment from 'moment';
import GraphicEqIcon from '@material-ui/icons/GraphicEq';
import styles from './MessageBubble.module.css';
import { convertPhoneFormat, employeeAvatarLink } from '../helper';
import InitialsAvatar from './InitialsAvatar';
import ReplaceEmoji from './Emoji';

class MessageBubble extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mediaDownloadFailed: false,
      mediaUrl: null,
      senderObj: null,
      resolved: props.message.state.attributes.resolved,
    };
  }

  componentDidMount = () => {
    const {
      message,
      message: {
        state: { type, media },
      },
    } = this.props;
    this.getParticipantDetails();
    if (type === 'media') {
      media
        .getContentTemporaryUrl()
        .then((url) => {
          this.setState({ mediaUrl: url });
        })
        .catch(() => this.setState({ mediaDownloadFailed: true }));
    }
    message.on('updated', ({ message: updatedMessage }) => {
      this.setState({
        resolved: updatedMessage.state.attributes.resolved,
      });
    });
  };

  componentDidUpdate(prevProps) {
    const { message } = this.props;
    if (prevProps.message !== message) {
      this.getParticipantDetails();
    }
  }

  getParticipantDetails = () => {
    const { message } = this.props;
    message
      .getParticipant()
      .then((member) => {
        this.setState({ senderObj: member });
      })
      .catch(() => {
        // If sender is not a member of conversation (as with System Message or customers who have changed phone numbers), ignore warning
      });
  };

  getEmployeeTooltip = (author, source) => {
    if (source === 'admin_portal') {
      return (
        <>
          <span>{author}</span>
          <br />
          <span style={{ fontSize: 'x-small', fontStyle: 'italic' }}>
            Sent from Admin
          </span>
        </>
      );
    }
    if (source === 'technician_app') {
      return (
        <>
          <span>{author}</span>
          <br />
          <span style={{ fontSize: 'x-small', fontStyle: 'italic' }}>
            Sent from Tech App
          </span>
        </>
      );
    }
    return author;
  };

  render = () => {
    const { mediaDownloadFailed, mediaUrl, senderObj, resolved } = this.state;
    const {
      direction,
      message: {
        state: { attributes, type, author, body, timestamp, sid: messageSid },
        conversation: {
          channelState: { friendlyName: conversationFriendlyName },
        },
      },
    } = this.props;

    const hasMedia = type === 'media';
    const fromEmployee = attributes?.from_employee;
    const source = attributes?.source;
    const avatarImgUrl = senderObj?.state.attributes?.avatar_image_url;
    const senderName = senderObj?.state.attributes?.name;
    const displayName =
      senderName || (fromEmployee ? author : convertPhoneFormat(author));
    const showEmployeeAvatar =
      (direction === 'incoming' &&
        fromEmployee &&
        author !== 'System Message') ||
      direction === 'outgoing';
    const employeeTooltip = showEmployeeAvatar
      ? this.getEmployeeTooltip(author, source)
      : '';

    let itemStyle;
    let divStyle;
    let timestampStyle;

    if (author === 'System Message') {
      itemStyle = styles.outgoing_msg;
      divStyle = styles.zbot_sent_div;
      timestampStyle = styles.sent_timestamp;
    } else if (direction === 'incoming' && fromEmployee) {
      itemStyle = styles.outgoing_msg;
      divStyle = styles.employee_sent_div;
      timestampStyle = styles.sent_timestamp;
    } else if (direction === 'incoming' && !fromEmployee) {
      itemStyle = styles.received_msg_customer;
      divStyle = styles.received_customer_div;
      timestampStyle = styles.received_customer_timestamp;
    } else {
      itemStyle = styles.outgoing_msg;
      divStyle = styles.sent_div;
      timestampStyle = styles.sent_timestamp;
    }

    return (
      <li id={messageSid} className={itemStyle}>
        <div className={divStyle}>
          {direction === 'incoming' && !fromEmployee && (
            <Tooltip placement="left" title={convertPhoneFormat(author)}>
              <span style={{ marginRight: '10px' }}>
                <InitialsAvatar name={senderName || conversationFriendlyName} />
              </span>
            </Tooltip>
          )}
          <div>
            <strong>{displayName}</strong>
            <br />
            <div className={styles.medias}>
              {hasMedia && (
                <Media hasFailed={mediaDownloadFailed} url={mediaUrl} />
              )}
            </div>
            {ReplaceEmoji(body)}
            <span className={timestampStyle}>
              {moment(timestamp).format('M/D/YYYY h:mm a')}
            </span>
          </div>
          {author === 'System Message' && (
            <span style={{ marginLeft: '10px' }}>
              <GraphicEqIcon style={{ width: '35', fontSize: '35' }} />
            </span>
          )}
          {showEmployeeAvatar && (
            <Tooltip placement="right" title={employeeTooltip}>
              <span style={{ marginLeft: '10px' }}>
                {employeeAvatarLink(avatarImgUrl, 35)}
              </span>
            </Tooltip>
          )}
        </div>
        {resolved && (
          <Divider style={{ color: 'inherit' }}>
            Resolved by {resolved.changed_by} on{' '}
            {moment(resolved.date).format('M/D/YYYY h:mm a')}
          </Divider>
        )}
      </li>
    );
  };
}

class Media extends PureComponent {
  render = () => {
    const { hasFailed, url } = this.props;
    return (
      <div
        className={`${styles.media}${!url ? ` ${styles.placeholder}` : ''}`}
        onClick={() => {
          Modal.info({
            maskClosable: true,
            closable: true,
            centered: true,
            icon: null,
            okText: 'Close',
            width: '60%',
            content: (
              <div className={styles.picture_container}>
                <img
                  style={{ width: '100%', height: '100%' }}
                  src={url}
                  alt="media"
                />
              </div>
            ),
          });
        }}
      >
        {!url && !hasFailed && <Spin />}

        {hasFailed && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Icon type="warning" style={{ fontSize: '5em' }} />
            <p>Failed to load media</p>
          </div>
        )}

        {!hasFailed && url && (
          <div className={styles.media_icon}>
            <div
              className={styles.picture_preview}
              style={{ backgroundImage: `url(${url})`, zIndex: 2 }}
            />
          </div>
        )}
      </div>
    );
  };
}

Media.propTypes = {
  hasFailed: PropTypes.bool.isRequired,
  url: PropTypes.string,
};

export default MessageBubble;
